import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { db } from "../firebase";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";

interface FirestoreComment {
  id: string;
  name: string;
  uid: string;
  photoURL?: string;
  comment: string;
  createdAt?: { seconds: number; nanoseconds: number } | null;
}

export default function CommentsList() {
  const [items, setItems] = useState<FirestoreComment[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const q = query(collection(db, "comments"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const data: FirestoreComment[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      setItems(data);
      setCurrentIndex(0);
    });
    return () => unsub();
  }, []);

  function formatDateTime(ts?: { seconds: number; nanoseconds: number } | null) {
    try {
      if (!ts) return "";
      const date = new Date(ts.seconds * 1000 + Math.floor(ts.nanoseconds / 1e6));
      return new Intl.DateTimeFormat("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    } catch {
      return "";
    }
  }

  // Auto-slide every 5s
  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => {
      setCurrentIndex((p) => (p + 1) % items.length);
    }, 5000);
    return () => clearInterval(t);
  }, [items.length]);

  const active = items[currentIndex];

  return (
    <div className="mt-6">
      {items.length === 0 ? (
        <div className="rounded-2xl border border-white/15 bg-white/5 p-6 text-center text-white/70">Belum ada komentar.</div>
      ) : (
        <>
          <div className="overflow-hidden">
            <motion.div
              key={active?.id || "empty"}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              className="flex justify-center"
            >
              <div className="w-full max-w-4xl group relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-700" />
                <div className="relative bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/15">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 border border-white/20 flex items-center justify-center">
                      {active?.photoURL ? (
                        <img src={active.photoURL} alt={active.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white/80 text-sm font-semibold">{active?.name?.[0] || "?"}</span>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white/90">{active?.name}</div>
                      <div className="text-[11px] text-white/60 inline-flex items-center gap-1">
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-white/50"><path d="M12 8V12l3 1" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        {formatDateTime(active?.createdAt)}
                      </div>
                    </div>
                  </div>
                  <div className="text-white/80 text-sm leading-relaxed">{active?.comment}</div>
                  {typeof (active as any)?.rating === 'number' && (
                    <div className="mt-3 flex items-center gap-1">
                      {[1,2,3,4,5].map(n => (
                        <svg key={n} viewBox="0 0 24 24" className={(active as any).rating >= n ? 'w-4 h-4 text-yellow-400 fill-yellow-400' : 'w-4 h-4 text-white/30'}>
                          <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.786 1.401 8.169L12 18.896l-7.335 3.87 1.401-8.169L.132 9.211l8.2-1.193z"/>
                        </svg>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {items.length > 1 && (
            <div className="flex justify-center gap-3 mt-6">
              {items.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`h-3 rounded-full transition-all duration-500 ${
                    idx === currentIndex ? 'w-10 bg-gradient-to-r from-cyan-400 to-purple-400' : 'w-3 bg-white/30 hover:bg-white/50 hover:w-6'
                  }`}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

